// Third party library imports
import { inject, Injectable } from "@angular/core";
import { MatSnackBar, MatSnackBarConfig, MatSnackBarRef, SimpleSnackBar } from "@angular/material/snack-bar";

export type SnackBarType = 'success' | 'error' | 'warning' | 'info';

export interface SnackBarOptions {
    duration?: number;
    action?: string;
    horizontalPosition?: 'start' | 'center' | 'end' | 'left' | 'right';
    verticalPosition?: 'top' | 'bottom';
    panelClass?: string | string[];
}

@Injectable({
    providedIn: "root",
})
export class SnackBarService {
    private _snackBar = inject(MatSnackBar);

    // Default configurations for different types
    private readonly defaultConfigs: Record<SnackBarType, SnackBarOptions> = {
        success: {
            duration: 4000,
            action: 'Close',
            horizontalPosition: 'end',
            verticalPosition: 'top',
            panelClass: ['snackbar-success']
        },
        error: {
            duration: 7000,
            action: 'Close',
            horizontalPosition: 'end',
            verticalPosition: 'top',
            panelClass: ['snackbar-error']
        },
        warning: {
            duration: 5000,
            action: 'Close',
            horizontalPosition: 'end',
            verticalPosition: 'top',
            panelClass: ['snackbar-warning']
        },
        info: {
            duration: 4000,
            action: 'Close',
            horizontalPosition: 'end',
            verticalPosition: 'top',
            panelClass: ['snackbar-info']
        }
    };

    /**
     * Show a snackbar with the specified type and options
     */
    show(message: string, type: SnackBarType = 'info', options?: SnackBarOptions): MatSnackBarRef<SimpleSnackBar> {
        const defaultConfig = this.defaultConfigs[type];
        const mergedOptions = { ...defaultConfig, ...options };
        const config = this.buildConfig(type, options);
        return this._snackBar.open(message, mergedOptions.action, config);
    }

    /**
     * Show a success snackbar
     */
    showSuccess(message: string, options?: SnackBarOptions): MatSnackBarRef<SimpleSnackBar> {
        return this.show(message, 'success', options);
    }

    /**
     * Show an error snackbar
     */
    showError(message: string, options?: SnackBarOptions): MatSnackBarRef<SimpleSnackBar> {
        return this.show(message, 'error', options);
    }

    /**
     * Show a warning snackbar
     */
    showWarning(message: string, options?: SnackBarOptions): MatSnackBarRef<SimpleSnackBar> {
        return this.show(message, 'warning', options);
    }

    /**
     * Show an info snackbar
     */
    showInfo(message: string, options?: SnackBarOptions): MatSnackBarRef<SimpleSnackBar> {
        return this.show(message, 'info', options);
    }

    /**
     * Dismiss any currently open snackbar
     */
    dismiss(): void {
        this._snackBar.dismiss();
    }

    /**
     * Legacy method for backward compatibility
     * @deprecated Use showError or showInfo instead
     */
    open(message: string, type: 'error' | 'info' = 'info'): MatSnackBarRef<SimpleSnackBar> {
        return type === 'error' ? this.showError(message) : this.showInfo(message);
    }

    /**
     * Build the configuration for the snackbar
     */
    private buildConfig(type: SnackBarType, options?: SnackBarOptions): MatSnackBarConfig {
        const defaultConfig = this.defaultConfigs[type];
        const mergedOptions = { ...defaultConfig, ...options };

        return {
            duration: mergedOptions.duration,
            horizontalPosition: mergedOptions.horizontalPosition,
            verticalPosition: mergedOptions.verticalPosition,
            panelClass: mergedOptions.panelClass
        };
    }
}